import { useEffect } from 'react';
import { readJSONFromLocalStorage, writeJSONToLocalStorage } from 'shared/consent/localStorage';
import { formatISODate, safeParseISO } from 'shared/util/datetime-fns';
import { sanitize } from 'shared/util/sanitize';

export interface TjvrRequestPersonalInfo {
    firstName: string | null;
    lastName: string | null;
    dateOfBirth: Date | null;
    phoneCountryCode: string | null;
    phoneNumber: string | null;
    messageFromTenant: string | null;
}

export const LAST_SEARCH_STORAGE_KEY = 'fhh_tjvr_last_request';

const defaultValues: TjvrRequestPersonalInfo = {
    firstName: null,
    lastName: null,
    dateOfBirth: null,
    phoneCountryCode: null,
    phoneNumber: null,
    messageFromTenant: null,
};

export const getLastRequestPersonalInfo = (): TjvrRequestPersonalInfo => {
    const data = getLastRequestFromLocalStorage();

    return {
        firstName: data.firstName ? String(data.firstName) : null,
        lastName: data.lastName ? String(data.lastName) : null,
        dateOfBirth: safeParseISO(data.dateOfBirth) ?? null,
        phoneCountryCode: data.phoneCountryCode ? String(data.phoneCountryCode) : null,
        phoneNumber: data.phoneNumber ? String(data.phoneNumber) : null,
        messageFromTenant: data.messageFromTenant ? String(data.messageFromTenant) : null,
    };
};

export const storeLastRequestPersonalInfo = (state: TjvrRequestPersonalInfo) => {
    writeJSONToLocalStorage(LAST_SEARCH_STORAGE_KEY, sanitize(state, Object.keys(defaultValues)));
};

const getLastRequestFromLocalStorage = (): Record<string, unknown> => readJSONFromLocalStorage(LAST_SEARCH_STORAGE_KEY) ?? {};

type UseRestoreLastRequestPersonalInfoProps = {
    hasUrlParamsError: boolean;
    setFirstname: (value: string) => void;
    setSurname: (value: string) => void;
    setDateOfBirth: (value: string) => void;
    setLandCode: (value: string) => void;
    setPhone: (value: string) => void;
    setMessage: (value: string) => void;
};

export const useRestoreLastRequestPersonalInfo = ({
    hasUrlParamsError,
    setFirstname,
    setSurname,
    setDateOfBirth,
    setLandCode,
    setPhone,
    setMessage,
}: UseRestoreLastRequestPersonalInfoProps) => {
    useEffect(() => {
        if (!hasUrlParamsError) {
            const lastRequest = getLastRequestPersonalInfo();
            if (!lastRequest) return;

            if (lastRequest.firstName) setFirstname(lastRequest.firstName);
            if (lastRequest.lastName) setSurname(lastRequest.lastName);
            if (lastRequest.dateOfBirth) setDateOfBirth(formatISODate(lastRequest.dateOfBirth));
            if (lastRequest.phoneCountryCode) setLandCode(lastRequest.phoneCountryCode);
            if (lastRequest.phoneNumber) setPhone(lastRequest.phoneNumber);
            if (lastRequest.messageFromTenant) setMessage(lastRequest.messageFromTenant);
        }
    }, [hasUrlParamsError, setFirstname, setSurname, setDateOfBirth, setLandCode, setPhone, setMessage]);
};
